import * as React from 'react';
import { AuthState } from '../auth/auth.types';
import SelectField from 'material-ui/SelectField';
import MenuItem from 'material-ui/MenuItem';
import TextField from 'material-ui/TextField';
import { FormattedNumber } from 'react-intl';
import {
  PaymentsState,
  PaymentStatus,
  PaymentRecordDetail,
  CollectionStatus,
  PaymentMethodType
} from './Types';
import { isAdminAccount, isImpersonating } from '../admin/Util';
import { AdminState } from '../admin/admin.types';
import {
  ALL_PAYMENT_STATUS,
  SUCCEEDED,
  FAILED,
  RETURNED,
  SCHEDULED,
  COLLECTED,
  PAYMENT_METHOD_TYPES,
  ALL_COLLECTION_STATUS
} from './Constants';
import { isPaymentDisabled, isProgramDisabled, isItClientAccount } from '../util/permission.util';
import PaymentsDetailsModal from './paymentsDetailsModal';
import { IdleState } from '../idle/Types';
import { isAuthTokenExpired } from '../commons/AuthTokenValidator';
import { BusinessState } from '../business/business.types';
import ReactTable from 'react-table';
import { dateFromString } from '../util/date-util';
import CommonFeatures from '../commons/CommonFeatures';
import * as moment from 'moment';
import { ProgramsState, Program } from '../programs/Types';
import { overlayStyle, renderOverlayLoading } from '../util/Overlay';

type FilterOption = 'MEMBER_NAME' | 'PAYER_NAME' | 'PAYMENT_STATUS' | 'COLLECTION_STATUS' | 'TAG' | 'PROGRAM';

type TableDataType = {
  readonly paymentHistoryId: number
  readonly memberName: string
  readonly paymentType: string
  readonly amount: JSX.Element
  readonly paymentStatus: string
  readonly collectionStatus: string
  readonly chargeDate: string
  readonly remainingTransactions: number
  readonly description: string
};

type Props = {
  readonly authState: AuthState
  readonly adminState: AdminState
  readonly paymentsState: PaymentsState
  readonly businessState: BusinessState
  readonly idleState: IdleState
  readonly programsState: ProgramsState
  readonly getPaymentDetailRecords: (year: number, month: number) => Promise<Response>
  readonly toAdminDashboard: () => void
  readonly toDashboard: () => void
  readonly getPrograms: () => Promise<Response>
};

type States = {
  readonly yearFieldOpen: boolean
  readonly monthFieldOpen: boolean
  readonly year: number
  readonly currentYear: number
  readonly month: number
  readonly filteredData: PaymentRecordDetail[]
  readonly selectedTableRow: number
  readonly paymentDetailsModalOpen: boolean
  readonly loadedPaymentDetailRecords: boolean
  readonly filterOption: FilterOption
  readonly memberNameFilter: string
  readonly payerNameFilter: string
  readonly paymentStatusFilter: PaymentStatus[]
  readonly collectionStatusFilter: CollectionStatus[]
  readonly paymentMethodTypeFilter: PaymentMethodType[]
  readonly tagFilter: string
  readonly programFilter: string
  readonly loadedNewPaymentsList: boolean
};

class PaymentHistoryComponent extends React.Component<Props, States> {

  constructor(props: Props) {
    super(props);

    this.state = {
      yearFieldOpen: false,
      monthFieldOpen: false,
      year: new Date().getFullYear(),
      currentYear: new Date().getFullYear(),
      month: new Date().getMonth() + 1,
      filteredData: props.paymentsState.paymentRecordDetails ? props.paymentsState.paymentRecordDetails : [],
      selectedTableRow: 0,
      paymentDetailsModalOpen: false,
      loadedPaymentDetailRecords: false,
      filterOption: 'MEMBER_NAME',
      memberNameFilter: '',
      payerNameFilter: '',
      paymentStatusFilter: ALL_PAYMENT_STATUS,
      collectionStatusFilter: ALL_COLLECTION_STATUS,
      paymentMethodTypeFilter: PAYMENT_METHOD_TYPES,
      tagFilter: '',
      programFilter: '',
      loadedNewPaymentsList: false
    };

    this.handleYearFieldChange = this.handleYearFieldChange.bind(this);
    this.handleMonthFieldChange = this.handleMonthFieldChange.bind(this);
    this.handleMemberNameFilterChange = this.handleMemberNameFilterChange.bind(this);
    this.handlePayerNameFilterChange = this.handlePayerNameFilterChange.bind(this);
    this.filterPaymentRecordDetails = this.filterPaymentRecordDetails.bind(this);
    this.onFilterByPaymentStatusChange = this.onFilterByPaymentStatusChange.bind(this);
  }

  componentWillReceiveProps(nextProps: Props) {
    if (this.props !== nextProps && !isAuthTokenExpired() && nextProps.authState.statusCode === 200) {

      if (
        !isPaymentDisabled(nextProps.authState, nextProps.adminState) &&
        this.state.filteredData.length === 0 &&
        !this.state.loadedPaymentDetailRecords
      ) {
        this.setState(
          {loadedPaymentDetailRecords: true},
          () => nextProps.getPaymentDetailRecords(this.state.year, this.state.month)
        );
      }
      
      this.setState(
        {filteredData: nextProps.paymentsState.paymentRecordDetails},
        this.filterPaymentRecordDetails
      );
    }
  }

  componentWillUnmount() {
    this.setState({
      paymentDetailsModalOpen: false,
      loadedPaymentDetailRecords: false
    });
  }

  componentDidMount() {
    const { authState, getPaymentDetailRecords, toAdminDashboard, adminState, toDashboard } = this.props;
    const { year, month } = this.state;

    if (isAdminAccount(authState) && !isImpersonating(adminState)) {
      return toAdminDashboard();
    }

    if (!isProgramDisabled(authState, adminState) && !isAuthTokenExpired()) {
      this.props.getPrograms();
    }

    if (!isPaymentDisabled(authState, adminState)) {
      getPaymentDetailRecords(year, month)
      .then(response => {
        switch (this.props.paymentsState.statusCode) {
          case (200):
            this.setState({loadedNewPaymentsList: true});
            return;
          case (403):
            return toDashboard();
          default:
            return;
        }
      });
    } else {
      toDashboard();
    }
  }

  render() {
    const {
      selectedTableRow,
      paymentDetailsModalOpen,
      filteredData,
      currentYear,
      year,
      month
    } = this.state;

    const {
      authState,
      adminState,
      businessState
    } = this.props;

    return (
      <CommonFeatures
        authState={this.props.authState}
        businessState={this.props.businessState}
      >
        <div className="page-header-row row mt-2 mb-2">
          <div className="col-auto">
            <h3>Membership Payment Report</h3>
          </div>
        </div>

        <div className="row mb-2">
          <div className="col-auto">
            <SelectField
              floatingLabelText="Year"
              value={year}
              onChange={this.handleYearFieldChange}
            >
              <MenuItem value={currentYear + 1} primaryText={currentYear + 1} />
              <MenuItem value={currentYear} primaryText={currentYear} />
              <MenuItem value={currentYear - 1} primaryText={currentYear - 1} />
              <MenuItem value={currentYear - 2} primaryText={currentYear - 2} />
              <MenuItem value={currentYear - 3} primaryText={currentYear - 3} />
            </SelectField>
          </div>
          <div className="col-auto">
            <SelectField
              floatingLabelText="Month"
              value={month}
              onChange={this.handleMonthFieldChange}
            >
              <MenuItem value={1} primaryText="January" />
              <MenuItem value={2} primaryText="February" />
              <MenuItem value={3} primaryText="March" />
              <MenuItem value={4} primaryText="April" />
              <MenuItem value={5} primaryText="May" />
              <MenuItem value={6} primaryText="June" />
              <MenuItem value={7} primaryText="July" />
              <MenuItem value={8} primaryText="August" />
              <MenuItem value={9} primaryText="September" />
              <MenuItem value={10} primaryText="October" />
              <MenuItem value={11} primaryText="November" />
              <MenuItem value={12} primaryText="December" />
            </SelectField>
          </div>
        </div>

        <div className="row mb-2 align-items-center">
          <div className="col-auto">
            <SelectField
              multiple={true}
              floatingLabelText="Payment Type"
              value={this.state.paymentMethodTypeFilter}
              onChange={this.onFilterByPaymentMethodTypeChange}
              style={{float: 'left'}}
            >
              {
                PAYMENT_METHOD_TYPES.map((p: PaymentMethodType, index: number) => {
                  return <MenuItem key={index} value={p} primaryText={this.getTextValueOfPaymentMethodType(p)} />;
                })
              }
            </SelectField>
          </div>
          <div className="col-auto">
            <SelectField
              floatingLabelText="Filter Option"
              value={this.state.filterOption}
              onChange={this.handleFilterOptionChange}
              style={{float: 'left'}}
              autoWidth={true}
            >
              <MenuItem value={'MEMBER_NAME'} primaryText={'Member Name'} />
              <MenuItem value={'PAYER_NAME'} primaryText={'Payer Name'} />
              <MenuItem value={'PAYMENT_STATUS'} primaryText={'Payment Status'} />
              {this.props.paymentsState.paymentRecordDetails
                .filter((p: PaymentRecordDetail) => p.collectionStatus).length > 0 ?
                <MenuItem value={'COLLECTION_STATUS'} primaryText={'Collection Status'} /> :
                undefined
              }
              <MenuItem value={'TAG'} primaryText={'Tag'} />
              <MenuItem value={'PROGRAM'} primaryText={'Program'} />
            </SelectField>
          </div>
          <div className="col-auto">
            {this.renderFilterInputField()}
          </div>
        </div>
        
        {
          isItClientAccount(authState, adminState) || isImpersonating(adminState) ?
            <div className="row mb-4 mt-4 animated fadeIn">
              <div className="col-12">
                {this.renderSummary()}
              </div>
            </div> :
            null
        }

        <div className="row mb-4 mt-4 animated fadeIn">
          <div
            className="col-12"
            style={overlayStyle(this.state.loadedNewPaymentsList)}
          >
            {renderOverlayLoading(this.state.loadedNewPaymentsList, '25%')}
            {this.renderMembershipPaymentsTable()}
          </div>
        </div>
        {
          (filteredData.length > selectedTableRow) ? (
            <PaymentsDetailsModal
              businessState={businessState}
              paymentRecordDetail={filteredData[selectedTableRow]}
              modalOpen={paymentDetailsModalOpen}
              togglePaymentDetailsModalOpen={this.togglePaymentDetailsModalOpen}
            />
          ) : null
        }
      </CommonFeatures>
    );
  }

  private renderFilterInputField = (): JSX.Element => {
    switch (this.state.filterOption) { 
      case 'MEMBER_NAME': {
        return(
          <TextField
            hintText="Filter by member name"
            floatingLabelText="Member Name"
            value={this.state.memberNameFilter}
            onChange={this.handleMemberNameFilterChange}
          />
        );
      }
      case 'PAYER_NAME': {
        return(
          <TextField
            hintText="Filter by payer name"
            floatingLabelText="Payer Name"
            value={this.state.payerNameFilter}
            onChange={this.handlePayerNameFilterChange}
          />
        );
      }
      case 'PAYMENT_STATUS': {
        return(
          <SelectField
            fullWidth={false}
            multiple={true}
            floatingLabelText="Payment Status"
            value={this.state.paymentStatusFilter}
            onChange={this.onFilterByPaymentStatusChange}
          >
            {
              ALL_PAYMENT_STATUS.map((paymentStatus: PaymentStatus, index) => {
                return <MenuItem 
                  key={index} 
                  value={paymentStatus} 
                  primaryText={this.getTextValueOfPaymentStatus(paymentStatus)} 
                />;
              })
            }
          </SelectField>
        );
      }
      case 'COLLECTION_STATUS': {
        return(
          <SelectField
            fullWidth={false}
            multiple={true}
            floatingLabelText="Collection Status"
            value={this.state.collectionStatusFilter}
            onChange={this.onFilterByCollectionStatusChange}
          >
            {
              ALL_COLLECTION_STATUS.map((c: CollectionStatus, index) => {
                return <MenuItem 
                  key={index} 
                  value={c} 
                  primaryText={this.getTextValueOfCollectionStatus(c)} 
                />;
              })
            }
          </SelectField>
        );
      }
      case 'TAG': {
        return(
          <SelectField
            fullWidth={false}
            floatingLabelText="Tag"
            value={this.state.tagFilter}
            onChange={this.onFilterByTagChange}
          >
            <MenuItem value={''} primaryText={''} />
            {
              this.props.businessState.business.tags.map((t: string, index) => {
                return <MenuItem 
                  key={index} 
                  value={t} 
                  primaryText={t}
                />;
              })
            }
          </SelectField>
        );
      }
      case 'PROGRAM': {
        if (isProgramDisabled(this.props.authState, this.props.adminState)) {
          return(
            <div className="red mt-4">
                Not enough permission to use this filter
            </div>
          );
        } else {
          return(
            <SelectField
              fullWidth={false}
              floatingLabelText="Program"
              value={this.state.programFilter}
              onChange={this.onFilterByProgramChange}
              disabled={isProgramDisabled(this.props.authState, this.props.adminState)}
            >
              <MenuItem value={''} primaryText={''} />
              {
                isProgramDisabled(this.props.authState, this.props.adminState) ?
                  undefined :
                  this.renderProgramItems()
              }
            </SelectField>
          );
        }
      }
      default:
        return undefined;
    }
  }

  private renderProgramItems = (): JSX.Element[] => {
    return(
      this.props.programsState.programs.map((p: Program, key: number) => {
        return <MenuItem key={key} value={p.name} primaryText={p.name} />;
      })
    );
  }

  private handleFilterOptionChange = (event: React.SyntheticEvent<{}>, index: number, value: FilterOption): void =>
    this.setState(
      {
        filterOption: value,
        filteredData: this.props.paymentsState.paymentRecordDetails,
        memberNameFilter: '',
        payerNameFilter: '',
        tagFilter: '',
        programFilter: '',
        paymentStatusFilter: ALL_PAYMENT_STATUS,
        collectionStatusFilter: ALL_COLLECTION_STATUS
      },
      this.filterPaymentRecordDetails
    )

  private onFilterByPaymentMethodTypeChange = (
    event: React.TouchEvent<HTMLSelectElement>,
    index: number,
    value: PaymentMethodType[]
  ): void => this.setState(
    {paymentMethodTypeFilter: value},
    this.filterPaymentRecordDetails
  )

  private renderSummary = (): JSX.Element => {
    return(
      <div>
        <span>
          <strong>Total Transactions :</strong>{' '}
          {this.state.filteredData.length}
        </span>
        &nbsp;&nbsp;&nbsp;&nbsp;
        <span>
          <strong>Collected Amount :</strong>{' '}
          {
            <FormattedNumber
              value={
                this.state.filteredData
                .filter((paymentRecordDetail: PaymentRecordDetail) => 
                  paymentRecordDetail.paymentStatus === SUCCEEDED ||
                  paymentRecordDetail.paymentStatus === RETURNED ||
                  (paymentRecordDetail.paymentStatus === FAILED && paymentRecordDetail.collectionStatus === COLLECTED)
                )
                .map((paymentRecordDetail: PaymentRecordDetail) => paymentRecordDetail.amount)
                .reduce((sum: number, value: number): number => sum + value, 0)
              }
              style="currency"
              currency="USD"
            />
          }
        </span>
        &nbsp;&nbsp;&nbsp;&nbsp;
        <span>
          <strong>Total Amount :</strong>{' '}
          {
            <FormattedNumber
              value={this.state.filteredData
                .map((paymentRecordDetail: PaymentRecordDetail) => paymentRecordDetail.amount)
                .reduce((sum: number, value: number): number => sum + value, 0)
              }
              style="currency"
              currency="USD"
            />
          }
        </span>
      </div>
    );
  }

  private createTableData = (p: PaymentRecordDetail): TableDataType => {
    return {
      paymentHistoryId: p.paymentHistoryId,
      memberName: p.memberName,
      paymentType: this.getTextValueOfPaymentMethodType(p.paymentMethodType),
      amount: <FormattedNumber value={p.amount} style="currency" currency="USD" />,
      paymentStatus: this.getTextValueOfPaymentStatus(p.paymentStatus),
      collectionStatus: this.getTextValueOfCollectionStatus(p.collectionStatus),
      chargeDate: moment(new Date(p.chargeDate)).format('MMM Do YY'),
      remainingTransactions: p.remainingTransactions,
      description: p.description
    };
  }

  private renderMembershipPaymentsTable = (): JSX.Element => {
    return(
      <ReactTable
        data={ !this.state.loadedNewPaymentsList ?
          [] :
          this.state.filteredData.map((p: PaymentRecordDetail) => this.createTableData(p))
        }
        columns={[
          {
            accessor: 'memberName',
            Header: 'Member Name',
            style: {textAlign: 'center'}
          },
          {                                                                                       
            accessor: 'paymentType',
            Header: 'Payment Type',
            style: {textAlign: 'center'}
          },
          {
            accessor: 'amount',
            Header: 'Amount',
            style: {textAlign: 'center'},
            sortMethod: (a, b, desc) => {
              if (a.props.value > b.props.value) {
                return 1;
              } else if (a.props.value < b.props.value) {
                return -1;
              } else {
                return 0;
              }
            }
          },
          {
            accessor: 'paymentStatus',
            Header: 'Payment Status',
            style: {textAlign: 'center'},
            getProps: (state, rowInfo, column) => {
              return {
                style: {
                  color: this.pickColor(rowInfo ? rowInfo.row.paymentStatus : 'Scheduled')
                }
              };
            }
          },
          {
            accessor: 'collectionStatus',
            Header: 'Collection Status',
            style: {textAlign: 'center'}
          },
          {
            accessor: 'chargeDate',
            Header: 'Charge Date',
            style: {textAlign: 'center'},
            sortMethod: (a, b, desc) => {
              if (dateFromString(a) > dateFromString(b)) {
                return 1;
              } else if (dateFromString(a) < dateFromString(b)) {
                return -1;
              } else {
                return 0;
              }
            }
          },
          {
            accessor: 'remainingTransactions',
            Header: 'RMNG TXN',
            style: {textAlign: 'center'}
          },
          {
            accessor: 'description',
            Header: 'Desc',
            style: {textAlign: 'center'}
          }
        ]}
        defaultPageSize={20}
        className="-striped -highlight"
        keyField={'paymentHistoryId'}
        getTdProps={(state, rowInfo, column, instance) => {
          return {
            onClick: (e, handleOriginal) => rowInfo ? this.handleCellClick(rowInfo.index) : null
          };
        }}
      />
    );
  }

  private pickColor = (paymentStatus: string): string => {
    switch (paymentStatus) {
      case 'Succeeded':
        return 'blue';
      case 'Failed':
        return 'red';
      case 'Deposited':
        // light green-ish
        return '#00ff00';
      default:
        return 'black';
    }
  }

  private onFilterByPaymentStatusChange = (
    event: React.TouchEvent<HTMLSelectElement>, index: number, value: PaymentStatus[]
  ): void => this.setState({paymentStatusFilter: value}, this.filterPaymentRecordDetails)

  private onFilterByTagChange = (event: React.TouchEvent<HTMLSelectElement>, index: number, value: string) => {
    this.setState({tagFilter: value}, this.filterPaymentRecordDetails);
  }

  private onFilterByProgramChange = (event: React.TouchEvent<HTMLSelectElement>, index: number, value: string) => {
    this.setState({programFilter: value}, this.filterPaymentRecordDetails);
  }

  private onFilterByCollectionStatusChange = (
    event: React.TouchEvent<HTMLSelectElement>, index: number, value: CollectionStatus[]
  ): void => this.setState({collectionStatusFilter: value}, this.filterPaymentRecordDetails)

  private filterPaymentRecordDetails = (): void => {
    if (this.props.paymentsState.paymentRecordDetails !== null) {
      this.setState({filteredData: this.applyFilters()});
    } else {
      return;
    }
  }

  private applyFilters = (): PaymentRecordDetail[] => {
    const { paymentsState } = this.props;
    const {
      payerNameFilter,
      memberNameFilter,
      paymentMethodTypeFilter,
      paymentStatusFilter,
      collectionStatusFilter,
      tagFilter,
      programFilter
    } = this.state;

    const filtered = paymentsState.paymentRecordDetails.filter((p: PaymentRecordDetail) =>
      p.memberName.toLowerCase().includes(memberNameFilter.toLowerCase()) &&
      p.payerName.toLowerCase().includes(payerNameFilter.toLowerCase()) &&
      paymentMethodTypeFilter.includes(p.paymentMethodType) &&
      paymentStatusFilter.includes(p.paymentStatus)
    );

    const extraFiltered = filtered.filter((p: PaymentRecordDetail) => {
      if (tagFilter) {

        return p.tags.map((t: string) => t.toLocaleLowerCase()).includes(tagFilter.toLowerCase());
      } else if (programFilter) {
        return p.programNames.map((t: string) => t.toLocaleLowerCase()).includes(programFilter.toLowerCase());
      } else {
        return true;
      }
    });
    
    if (paymentsState.paymentRecordDetails.filter((p: PaymentRecordDetail) => p.collectionStatus).length > 0) {
      return extraFiltered.filter((p: PaymentRecordDetail) => p.collectionStatus ? 
        collectionStatusFilter.includes(p.collectionStatus) :
        collectionStatusFilter.includes('NOT_REQUESTED'));
    } else {
      return extraFiltered;
    }
  }

  private handleMemberNameFilterChange = (event: React.FormEvent<HTMLInputElement>, newValue: string): void =>
    this.setState({memberNameFilter: newValue}, this.filterPaymentRecordDetails)

  private handlePayerNameFilterChange = (event: React.FormEvent<HTMLInputElement>, newValue: string): void =>
    this.setState({payerNameFilter: newValue}, this.filterPaymentRecordDetails)

  private handleYearFieldChange = (event: React.SyntheticEvent<{}>, index: number, value: number) => {
    const { getPaymentDetailRecords } = this.props;
    
    if (isAuthTokenExpired()) {
      getPaymentDetailRecords(this.state.year, this.state.month);
    } else {
      this.setState(
        {year: value, loadedNewPaymentsList: false},
        () => { 
          getPaymentDetailRecords(this.state.year, this.state.month)
          .then(res => {
            switch (this.props.paymentsState.statusCode) {
              case 200:
                this.setState({loadedNewPaymentsList: true});
                break;
              default:
                break;
            }
          }); 
        }
      );
    }
  }

  private handleMonthFieldChange = (event: React.SyntheticEvent<{}>, index: number, value: number) => {
    const { getPaymentDetailRecords } = this.props;

    if (isAuthTokenExpired()) {
      getPaymentDetailRecords(this.state.year, this.state.month);
    } else {
      this.setState(
        {month: value, loadedNewPaymentsList: false},
        () => { 
          getPaymentDetailRecords(this.state.year, this.state.month)
          .then(res => {
            switch (this.props.paymentsState.statusCode) {
              case 200:
                this.setState({loadedNewPaymentsList: true});
                break;
              default:
                break;
            }
          }); 
        }
      );
    }
  }

  private getTextValueOfPaymentMethodType = (paymentMethodType: PaymentMethodType): string => {
    if (paymentMethodType === 'CASH') {
      return 'Cash';
    } else if (paymentMethodType === 'VOIDCHEQUE') {
      return 'E.F.T.';
    } else {
      return 'Credit Card';
    }
  }

  private getTextValueOfPaymentStatus = (paymentStatus: PaymentStatus): string => {
    if (paymentStatus === 'SCHEDULED') {
      return 'Scheduled';
    } else if (paymentStatus === 'SUBMITTED') {
      return 'Submitted';
    } else if (paymentStatus === 'SUCCEEDED') {
      return 'Succeeded';
    } else if (paymentStatus === 'FAILED') {
      return 'Failed';
    } else {
      return 'Deposited';
    }
  }

  private getTextValueOfCollectionStatus = (collectionStatus: CollectionStatus): string => {
    if (collectionStatus === 'NOT_REQUESTED') {
      return 'Not Requested';
    } else if (collectionStatus === 'REQUESTED') {
      return 'Requested';
    } else if (collectionStatus === 'IN_PROGRESS') {
      return 'In Progress';
    } else if (collectionStatus === 'CLOSED') {
      return 'Closed';
    } else if (collectionStatus === 'COLLECTED') {
      return 'Collected';
    } else {
      return '';
    }
  }

  private handleCellClick = (row: number): void =>
    this.setState(
      {selectedTableRow: row},
      this.togglePaymentDetailsModalOpen
    )

  private togglePaymentDetailsModalOpen = (): void =>
    this.setState({paymentDetailsModalOpen: !this.state.paymentDetailsModalOpen})
}

export default PaymentHistoryComponent;



// WEBPACK FOOTER //
// ./src/payments/PaymentHistoryComponent.tsx